import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const getApiUrl = (path: string) => {
    if (!path) return '';

    const isUpload = path.startsWith('/uploads') || path.startsWith('uploads') || path.includes('/api/uploads');
    const isNative = typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform?.();
    const isWeb = typeof window !== 'undefined' && !isNative;

    // Remove backend domain if it's an absolute URL
    let cleanPath = path;
    if (cleanPath.startsWith('http')) {
        try {
            const urlObj = new URL(cleanPath);
            // If the URL points to our backend API, strip the origin
            if (urlObj.hostname.includes('petsmartmonitor.com') || urlObj.hostname === 'localhost' || urlObj.hostname.includes('run.place')) {
                cleanPath = urlObj.pathname + urlObj.search;
            }
        } catch (e) {
            // Ignore parse errors
        }
    }
    
    if (cleanPath.startsWith('data:')) return cleanPath;

    if (isWeb) {
        // On web, always return relative path to support sandbox/reverse proxies and prevent CSP blocks
        cleanPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
        if (isUpload) {
            // Rewrite /uploads to /api/uploads for reverse proxy compatibility
            if (cleanPath.startsWith('/uploads/')) {
                cleanPath = cleanPath.replace('/uploads/', '/api/uploads/');
            } else if (cleanPath.startsWith('uploads/')) {
                cleanPath = cleanPath.replace('uploads/', 'api/uploads/');
            }
        }
        
        // FIX: If running frontend locally (localhost) but pointing to Sandbox/Production API,
        // we must return the absolute remote URL for images, otherwise they fetch from the local server.
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
            const envApiUrl = import.meta.env.VITE_API_URL || localStorage.getItem('CUSTOM_API_URL');
            if (envApiUrl && envApiUrl.startsWith('http') && !envApiUrl.includes('localhost') && !envApiUrl.includes('127.0.0.1')) {
                const cleanBase = envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl;
                if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api/')) {
                    cleanPath = cleanPath.replace('/api/', '/');
                }
                return `${cleanBase}${cleanPath}`;
            }
        }

        return cleanPath;
    }

    let baseUrl = import.meta.env.VITE_API_URL || '';
    if (typeof window !== 'undefined') {
        const override = localStorage.getItem('CUSTOM_API_URL');
        if (override) {
            baseUrl = override;
        }
    }
    if (!baseUrl) {
        const isCapacitor = typeof window !== 'undefined' && (window as any).Capacitor;
        if (isCapacitor) {
            if (import.meta.env.DEV) {
                baseUrl = 'http://localhost:5001/api';
            } else {
                baseUrl = 'https://www.petsmartmonitor.com/api';
            }
        }
    }

    cleanPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;

    if (isUpload) {
        baseUrl = baseUrl.replace(/\/api\/?$/, '');
    }

    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    
    // Prevent double /api/api if the path already includes /api
    if (cleanBaseUrl.endsWith('/api') && cleanPath.startsWith('/api/')) {
        cleanPath = cleanPath.replace('/api/', '/');
    }

    return `${cleanBaseUrl}${cleanPath}`;
};

export const safeParseTags = (tags: string | undefined | null): string[] => {
    if (!tags) return [];
    
    // If it's already an array (unlikely from API but good for defense)
    if (Array.isArray(tags)) return tags;

    try {
        const parsed = JSON.parse(tags);
        if (Array.isArray(parsed)) return parsed;
        if (typeof parsed === 'string') return [parsed];
        return [];
    } catch (e) {
        // Fallback: If it's not valid JSON, treat it as a comma-separated string
        // This handles cases like "adams,ny,local"
        return tags.split(',').map(tag => tag.trim()).filter(Boolean);
    }
};
